import { useState } from "react";
import { Loader2, MailCheck, ArrowRight, Lock } from "lucide-react";
import { sendMagicLink, signInWithPassword } from "../services/supabase";
import { useT } from "../services/i18n";

export default function LoginScreen() {
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [pwdMode, setPwdMode] = useState(false);
  const [password, setPassword] = useState("");
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdError, setPwdError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || sending) return;
    setSending(true);
    setError("");
    try {
      await sendMagicLink(email);
      setSent(true);
    } catch (err) {
      if (err?.status === 429 || err?.code === "over_email_send_rate_limit") {
        setError(t("login_error_rate"));
      } else if (err?.code === "email_address_invalid") {
        setError(t("login_error_invalid"));
      } else {
        setError(t("login_error"));
      }
    } finally {
      setSending(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password || pwdLoading) return;
    setPwdLoading(true);
    setPwdError("");
    try {
      await signInWithPassword(email, password);
      // Succès : onAuthStateChange (App.js) prend le relais et ouvre l'app.
    } catch (err) {
      setPwdError(t("login_pwd_error"));
    } finally {
      setPwdLoading(false);
    }
  };

  return (
    <div
      className="flex min-h-[100dvh] flex-col items-center justify-center px-8 pb-[env(safe-area-inset-bottom)]"
      data-testid="login-screen"
    >
      <img
        src="/butterfly-nobg.png"
        alt=""
        className="mb-6 h-24 w-24 object-contain"
        data-testid="login-butterfly"
      />
      <h1 className="brand mb-2 text-3xl font-bold text-[var(--rmr-text)]">
        rememor<span className="text-[var(--rmr-accent)]">.ai</span>
      </h1>

      {sent ? (
        <div className="mt-6 flex max-w-xs flex-col items-center text-center" data-testid="login-sent">
          <span className="mb-4 grid h-14 w-14 place-items-center rounded-full bg-[var(--rmr-chip)] text-[var(--rmr-accent)]">
            <MailCheck size={28} />
          </span>
          <p className="text-[15px] leading-relaxed text-[var(--rmr-text)]">{t("login_sent")}</p>
          <button
            data-testid="login-resend-btn"
            onClick={() => {
              setSent(false);
              setEmail("");
            }}
            className="mt-6 text-sm font-semibold text-[var(--rmr-accent)]"
          >
            {t("login_other_email")}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-6 w-full max-w-xs" data-testid="login-form">
          <p className="mb-6 text-center text-sm leading-relaxed text-[var(--rmr-text-2)]">
            {t("login_subtitle")}
          </p>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("login_email_placeholder")}
            data-testid="login-email-input"
            className="w-full rounded-2xl border border-[var(--rmr-border)] bg-[var(--rmr-surface)] px-4 py-3.5 text-[15px] text-[var(--rmr-text)] outline-none transition-colors focus:border-[var(--rmr-accent)]"
          />
          {error && (
            <p className="mt-2 text-center text-sm text-[#B4554F]" data-testid="login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={sending || !email.trim()}
            data-testid="login-submit-btn"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--rmr-accent)] py-3.5 text-[15px] font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-50"
          >
            {sending ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
            {t("login_cta")}
          </button>
        </form>
      )}

      {!sent && (
        <div className="mt-5 w-full max-w-xs">
          {!pwdMode ? (
            <button
              type="button"
              data-testid="login-password-toggle"
              onClick={() => {
                setPwdMode(true);
                setPwdError("");
              }}
              className="mx-auto flex items-center justify-center gap-1.5 text-sm font-medium text-[var(--rmr-text-3)] transition-colors hover:text-[var(--rmr-accent)]"
            >
              <Lock size={14} /> {t("login_pwd_link")}
            </button>
          ) : (
            <form onSubmit={handlePasswordLogin} data-testid="login-password-form" className="space-y-3">
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("login_email_placeholder")}
                data-testid="login-password-email-input"
                className="w-full rounded-2xl border border-[var(--rmr-border)] bg-[var(--rmr-surface)] px-4 py-3.5 text-[15px] text-[var(--rmr-text)] outline-none transition-colors focus:border-[var(--rmr-accent)]"
              />
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("login_pwd_password_placeholder")}
                data-testid="login-password-input"
                className="w-full rounded-2xl border border-[var(--rmr-border)] bg-[var(--rmr-surface)] px-4 py-3.5 text-[15px] text-[var(--rmr-text)] outline-none transition-colors focus:border-[var(--rmr-accent)]"
              />
              {pwdError && (
                <p className="text-center text-sm text-[#B4554F]" data-testid="login-password-error">
                  {pwdError}
                </p>
              )}
              <button
                type="submit"
                disabled={pwdLoading || !email.trim() || !password}
                data-testid="login-password-submit-btn"
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--rmr-accent)] py-3.5 text-[15px] font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-50"
              >
                {pwdLoading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />}
                {t("login_pwd_cta")}
              </button>
              <button
                type="button"
                data-testid="login-password-hide"
                onClick={() => {
                  setPwdMode(false);
                  setPassword("");
                  setPwdError("");
                }}
                className="mx-auto block text-xs font-medium text-[var(--rmr-text-3)] transition-colors hover:text-[var(--rmr-accent)]"
              >
                {t("login_pwd_hide")}
              </button>
            </form>
          )}
        </div>
      )}


      <p className="mt-10 text-center text-xs leading-relaxed text-[var(--rmr-text-3)]">
        {t("login_note")}
      </p>
    </div>
  );
}
